<template>
	<div>
		<div class="font-semibold mb-3 text-ihh-midnight-gray text-sm">
			<span>{{ modifier.displayName }}:</span>
			<span v-if="!modifier?.required" class="text-ihh-slate-gray font-normal ml-1">Optional</span>
		</div>

		<div class="flex flex-col gap-2">
			<div
				v-for="option in modifier.optionValues"
				:key="option.id"
				:class="[
					'group relative flex items-center gap-3 p-2 rounded-lg border transition-all duration-200',
					getCartQuantity(option)
						? 'bg-ihh-pink/30 border-ihh-dark-salmon/50'
						: mainProductInCart
							? 'bg-gray-50 border-gray-200 hover:border-ihh-dark-salmon/50'
							: isSelected(option)
								? 'bg-gradient-to-r from-ihh-pink to-ihh-purple border-ihh-dark-salmon cursor-pointer'
								: 'bg-gray-50 border-gray-200 hover:border-ihh-dark-salmon/50 cursor-pointer',
				]"
				@click="!mainProductInCart && !getCartQuantity(option) && toggleSelection(option)"
			>
				<div class="relative w-14 h-14 shrink-0">
					<img
						v-if="getThumbnailImage(option.valueData.productId[0].images)"
						:src="getThumbnailImage(option.valueData.productId[0].images)"
						:alt="option.label"
						class="w-full h-full object-cover rounded-md"
					/>
				</div>

				<div class="flex-1 min-w-0">
					<div class="text-xs text-ihh-midnight-gray font-medium leading-tight line-clamp-2">{{ option.label }}</div>
					<div class="text-xs text-ihh-slate-gray mt-0.5">{{ formatPrice(option.valueData.productId[0].currentPrice) }}</div>
				</div>

				<div class="shrink-0" @click.stop>
					<template v-if="getCartQuantity(option)">
						<div class="flex bg-ihh-dark-salmon rounded-full items-center text-white font-semibold text-xs">
							<button @click="removeFromCart(option)" class="rounded-full w-6 h-6 hover:bg-ihh-salmon/50 flex items-center justify-center">-</button>
							<div class="px-1 text-center min-w-[1rem]">{{ getCartQuantity(option) }}</div>
							<button @click="addToCart(option)" class="rounded-full w-6 h-6 bg-ihh-salmon/50 hover:bg-ihh-salmon/80 flex items-center justify-center">+</button>
						</div>
					</template>
					<template v-else-if="mainProductInCart">
						<button
							@click="addToCart(option)"
							class="text-xs font-medium text-ihh-midnight-gray py-1.5 px-3 rounded-full bg-ihh-dark-salmon hover:bg-ihh-salmon transition"
						>
							+ Add
						</button>
					</template>
					<template v-else>
						<div
							:class="[
								'w-5 h-5 rounded border-2 flex items-center justify-center transition-all',
								isSelected(option) ? 'bg-ihh-dark-salmon border-ihh-dark-salmon' : 'border-gray-300 bg-white',
							]"
							@click="toggleSelection(option)"
						>
							<svg v-if="isSelected(option)" class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
							</svg>
						</div>
					</template>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'ProductModifier',
	inject: ['store'],
	props: {
		modifier: {
			type: Object,
			required: true,
		},
		mainProduct: {
			type: Object,
			default: null,
		},
	},
	data() {
		return {
			selectedOptions: [],
		}
	},
	methods: {
		getThumbnailImage(images) {
			const thumbnail = images.find((img) => img.isThumbnail)
			return thumbnail ? thumbnail.url : ''
		},
		getCartQuantity(option) {
			const product = option.valueData.productId[0]
			return this.store.cart?.cartItems?.find?.((c) => c.item.id === product.id)?.quantity || 0
		},
		isSelected(option) {
			const product = option.valueData.productId[0]
			return this.selectedOptions.some((p) => p.id === product.id)
		},
		toggleSelection(option) {
			if (this.getCartQuantity(option)) return

			const product = option.valueData.productId[0]
			const index = this.selectedOptions.findIndex((p) => p.id === product.id)

			if (index >= 0) {
				this.selectedOptions.splice(index, 1)
			} else {
				this.selectedOptions.push(product)
			}

			this.$emit('selectionChanged', this.selectedOptions)
		},
		addToCart(option) {
			const product = option.valueData.productId[0]
			const existingItem = this.store.cart?.cartItems?.find?.((c) => c.item.id === product.id)

			if (existingItem) {
				existingItem.quantity += 1
			} else {
				this.store.cart?.cartItems?.push?.({
					item: product,
					quantity: 1,
					variant: product.variants?.[0],
				})
			}

			const index = this.selectedOptions.findIndex((p) => p.id === product.id)
			if (index >= 0) {
				this.selectedOptions.splice(index, 1)
				this.$emit('selectionChanged', this.selectedOptions)
			}
		},
		removeFromCart(option) {
			const product = option.valueData.productId[0]
			const existingItem = this.store.cart?.cartItems?.find?.((c) => c.item.id === product.id)

			if (!existingItem) return

			if (existingItem.quantity > 1) {
				existingItem.quantity -= 1
			} else {
				this.store.cart.cartItems = this.store.cart.cartItems.filter((c) => c.item.id !== product.id)
			}
		},
		formatPrice(price) {
			if (!price) return ''
			return price.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
		},
		getSelectedProducts() {
			return this.selectedOptions
		},
		clearSelections() {
			this.selectedOptions = []
			this.$emit('selectionChanged', this.selectedOptions)
		},
	},
	computed: {
		mainProductInCart() {
			if (!this.mainProduct?.id) return false
			return !!this.store.cart?.cartItems?.find?.((c) => c.item.id === this.mainProduct.id)
		},
	},
}
</script>

<style scoped></style>
